import { useEffect } from 'react';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';
import { READONLY_MESSAGE } from '../../lib/viewAs';

// Buttons that commit a change — greyed while viewing as another user in read-only mode.
const WRITE_RE = /\b(save|submit|delete|remove|upload|send|approve|reject|void|archive|restore|publish|confirm|create|update|complete|sign off|clock in|clock out|generate|import|sync|reset|promote|assign|convert|merge|post|pay|issue|record)\b/i;
const SAFE_RE = /\b(cancel|close|export|download|print|filter|search|back|view|open|exit|preview|sort|refresh|copy|share)\b/i;
const SELECTOR = 'button, input[type="submit"], input[type="file"], [role="button"]';

const labelOf = (el) => `${el.getAttribute('aria-label') || ''} ${el.getAttribute('title') || ''} ${el.textContent || ''} ${el.getAttribute('data-testid') || ''}`.trim();

const shouldGrey = (el) => {
  if (el.closest('[data-imp-banner]') || el.closest('[data-imp-safe]')) return false;
  if (el.tagName === 'INPUT' && el.type === 'file') return true;
  if (el.getAttribute('type') === 'submit') return true;
  const label = labelOf(el);
  return WRITE_RE.test(label) && !SAFE_RE.test(label);
};

/** Renders nothing — tags write buttons app-wide and blocks their clicks while a view-as session is read-only. */
export function ReadOnlyGuard() {
  const { viewAsReadOnly } = useAuth();

  useEffect(() => {
    if (!viewAsReadOnly) return undefined;
    document.body.classList.add('imp-readonly');
    let raf = 0;
    const scan = () => {
      raf = 0;
      document.querySelectorAll(SELECTOR).forEach((el) => {
        if (el.classList.contains('imp-ro')) return;
        if (!shouldGrey(el)) return;
        el.classList.add('imp-ro');
        el.setAttribute('aria-disabled', 'true');
        if (!el.getAttribute('title')) el.setAttribute('title', READONLY_MESSAGE);
        if (el.tagName === 'INPUT' && el.type === 'file') el.disabled = true;
      });
    };
    const queue = () => { if (!raf) raf = requestAnimationFrame(scan); };
    const obs = new MutationObserver(queue);
    obs.observe(document.body, { childList: true, subtree: true, characterData: true });
    scan();

    let lastToast = 0;
    const block = (e) => {
      const el = e.target?.closest?.('.imp-ro');
      if (!el) return;
      e.preventDefault();
      e.stopImmediatePropagation();
      if (Date.now() - lastToast > 1500) { lastToast = Date.now(); toast.error(READONLY_MESSAGE); }
    };
    document.addEventListener('click', block, true);
    document.addEventListener('submit', block, true);

    return () => {
      obs.disconnect();
      if (raf) cancelAnimationFrame(raf);
      document.removeEventListener('click', block, true);
      document.removeEventListener('submit', block, true);
      document.body.classList.remove('imp-readonly');
      document.querySelectorAll('.imp-ro').forEach((el) => {
        el.classList.remove('imp-ro');
        el.removeAttribute('aria-disabled');
        if (el.getAttribute('title') === READONLY_MESSAGE) el.removeAttribute('title');
        if (el.tagName === 'INPUT' && el.type === 'file') el.disabled = false;
      });
    };
  }, [viewAsReadOnly]);

  return null;
}
